import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/styles'
import { useTranslation } from 'react-i18next'
import BaseModal from '../../common/modals/BaseModal'
import { Grid } from '@material-ui/core'
import DazzedParagraph16 from '../../common/text/DazzedParagraph16'
import FrequencyPicker from '../../common/inputs/FrequencyPicker'
import {
  getPeriodTypeOptions,
  ScanFrequencyOptions,
  shouldOfferDoubleScanChoice,
  doubleScanPayload
} from '../../../utils/statusUtils'
import ArchivedStatusMessage from './ArchivedStatusMessage'
import SelectedStatus from './SelectedStatus'
import { isMobile } from 'utils/mobileUtils'
import TreatmentTypePicker from '../PatientCard/TreatmentTypePicker'
import { StatusTypes } from '@grin-rnd/grin-api-sdk/dist/Enums/Statuses'
import AlignersSets from '../PatientCard/AlignersSets'
import DoubleScanPicker from '../PatientCard/DoubleScanPicker'
import { ALIGNERS_TREATMENT_TYPE } from 'consts/treatmentConsts'
import GrinRadioGroup from '../../common/GrinRadioGroup'

const RetentionStlChoices = {
  UseLastStl: 'useLastStl',
  UploadLater: 'uploadLater'
}

const useStyles = makeStyles(() => ({
  root: {
    width: '750px',
    maxWidth: '750px'
  },
  subtitle: {
    fontWeight: 600,
    fontSize: '14px',
    width: '100%'
  },
  radioGroup: {
    justifyContent: 'center',
    flexDirection: isMobile() ? 'column' : 'row'
  },
  archivedMessage: {
    width: '350px'
  }
}))

const UpdateStatusModal = ({
  isOpen,
  selectedStatus,
  onCancel = () => {},
  onSubmit = () => {},
  onClose = () => {},
  isLoading = false,
  showRetentionStlPrompt = false,
  currentDoubleScanEnabled = false,
  currentTreatmentType
}) => {
  const classes = useStyles({ isMobile: isMobile() })
  const { t } = useTranslation()

  const [didSubmit, setDidSubmit] = useState(false)
  const [status, setStatus] = useState(null)
  const [selectedFrequency, setSelectedFrequency] = useState('')
  const [selectedCycleIntervalPeriodType, setSelectedCycleIntervalPeriodType] = useState('')
  const [treatmentType, setTreatmentType] = useState()
  const [totalAlignersSets, setTotalAlignersSets] = useState('')
  const [retentionStlChoice, setRetentionStlChoice] = useState(RetentionStlChoices.UseLastStl)
  const [isDoubleScan, setIsDoubleScan] = useState(false)

  const statusSettings = useMemo(() => JSON.parse(status?.settings || '{}'), [status])
  const frequencyOptions = useMemo(() => ScanFrequencyOptions[status?.type], [status])

  useEffect(() => {
    if (isOpen) {
      setStatus(selectedStatus)
      // Seeded from what the practice already chose. Defaulting to false here would send an
      // explicit false on any later status or frequency change and silently wipe a Double
      // setting the practice never touched -- and show them Single while doing it.
      setIsDoubleScan(currentDoubleScanEnabled)
      // Deliberately NOT seeded. This state means "what the practice picked in this modal", and
      // it also gates Save for Active Treatment - seeding it would pre-fill that picker and let
      // Save through without anyone choosing, which is a change to the aligners flow this work
      // is not meant to touch. The scan-type predicate reads effectiveTreatmentType instead.
      setTreatmentType()
    }
    // currentTreatmentType is deliberately not a dependency: the body no longer reads it, and
    // listing it means a type changed in the Scan Settings tab underneath re-runs this and wipes
    // the doctor's in-progress selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatus, isOpen, currentDoubleScanEnabled])

  const scanFrequencyDefaults = useMemo(() => JSON.parse(status?.scanFrequencyDefaults || '{}'), [status])

  const periodTypeOptions = useMemo(() => getPeriodTypeOptions(selectedStatus?.key), [selectedStatus])

  const subtitleText = useMemo(() => {
    if (!status?.hasLimitedScans) {
      return t('statuses.menu.subtitles.unlimited')
    }

    if (status?.maxScansPerPeriod > 0) {
      return t('statuses.menu.subtitles.limited', { maxScansPerPeriod: status.maxScansPerPeriod })
    }

    return t('statuses.menu.subtitles.scansNotAvailable')
  }, [status, t])

  const isRetentionPromptVisible = showRetentionStlPrompt && status?.type === StatusTypes.Retention

  // What the patient is on, whether or not it was picked here. TreatmentTypePicker only renders
  // for Active Treatment, so without the fallback the predicate sees undefined for every other
  // status and an existing "Other" patient moved to Observation, Transferred and so on is offered
  // no control at all - while the app still honours a stored flag for them.
  const effectiveTreatmentType = treatmentType ?? currentTreatmentType

  const isScanTypeVisible = useMemo(
    () => shouldOfferDoubleScanChoice({ treatmentType: effectiveTreatmentType, statusType: status?.type }),
    [status?.type, effectiveTreatmentType]
  )

  const handleSubmit = useCallback(() => {
    setDidSubmit(true)
    onSubmit({
      cycleInterval: selectedFrequency,
      cycleIntervalPeriodType: selectedCycleIntervalPeriodType,
      treatmentType,
      totalAlignersSets,
      // Through the same helper the Scan Settings tab uses. Inline, this surface had no
      // previousTreatmentType branch, so picking a new type here hid the control and left a stale
      // true in storage - which comes back to life the day the patient returns to Retention. The
      // identical change made from the other tab cleared it; the two must not disagree.
      ...doubleScanPayload({
        treatmentType: effectiveTreatmentType,
        statusType: status?.type,
        isDoubleScan,
        previousTreatmentType: currentTreatmentType
      }),
      ...(isRetentionPromptVisible
        ? { useLastStlForRetention: retentionStlChoice === RetentionStlChoices.UseLastStl }
        : {})
    })
  }, [
    onSubmit,
    selectedCycleIntervalPeriodType,
    selectedFrequency,
    totalAlignersSets,
    treatmentType,
    isRetentionPromptVisible,
    retentionStlChoice,
    isDoubleScan,
    effectiveTreatmentType,
    status?.type,
    currentTreatmentType
  ])

  useEffect(() => {
    if (isOpen && didSubmit && !isLoading) {
      onClose()
    }
  }, [didSubmit, isLoading, isOpen, onClose])

  useEffect(() => {
    if (!isOpen) {
      setDidSubmit(false)
      setRetentionStlChoice(RetentionStlChoices.UseLastStl)
    }
  }, [isOpen])

  useEffect(() => {
    setSelectedFrequency(scanFrequencyDefaults.periodLength)
    setSelectedCycleIntervalPeriodType(scanFrequencyDefaults.periodType)
  }, [scanFrequencyDefaults])

  useEffect(() => {
    if (treatmentType !== ALIGNERS_TREATMENT_TYPE) {
      setTotalAlignersSets(null)
    }
  }, [treatmentType])

  return (
    <BaseModal
      open={isOpen}
      primaryLabel={isMobile() ? t('general.save') : t('statuses.updateStatusModal.primaryButton')}
      secondaryLabel={t('statuses.updateStatusModal.secondaryButton')}
      onSecondaryBtnClick={onCancel}
      onPrimaryBtnClick={handleSubmit}
      isPrimaryDisabled={status?.type === StatusTypes.ActiveTreatment && !treatmentType}
      isLoading={isLoading}
      withCloseIcon
      handleClose={onCancel}
      className={classes.root}
      largerButtons={!isMobile()}
      title={t('statuses.updateStatusModal.title')}
      variant={isMobile() ? 'fullscreen' : undefined}
    >
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Grid container justifyContent="center">
            <Grid item xs={12}>
              <SelectedStatus type={status?.type} color={statusSettings.color} subtitleText={subtitleText} />
            </Grid>
            {status?.type === StatusTypes.ActiveTreatment && (
              <>
                <Grid item xs={10}>
                  <TreatmentTypePicker
                    treatmentType={treatmentType}
                    onSet={value => {
                      setTreatmentType(value)
                    }}
                    tooltip={false}
                    placeholder
                  />
                </Grid>
                {treatmentType === ALIGNERS_TREATMENT_TYPE && (
                  <Grid item xs={10}>
                    <AlignersSets alignersSets={totalAlignersSets} setAlignersSets={setTotalAlignersSets} />
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </Grid>
        {isScanTypeVisible && (
          <Grid item xs={12}>
            <Grid container justifyContent="center">
              <DazzedParagraph16 className={classes.subtitle} textAlign={isMobile() ? 'start' : 'center'}>
                {t('statuses.updateStatusModal.scanTypeSubtitle')}
              </DazzedParagraph16>
              <DoubleScanPicker
                radioGroupClassName={classes.radioGroup}
                isDoubleScan={isDoubleScan}
                onSet={setIsDoubleScan}
              />
            </Grid>
          </Grid>
        )}
        {status?.type !== 'archived' ? (
          <Grid item xs={12}>
            <Grid container>
              <DazzedParagraph16 className={classes.subtitle} textAlign={isMobile() ? 'start' : 'center'}>
                {t('statuses.updateStatusModal.subtitle')}
              </DazzedParagraph16>
              <FrequencyPicker
                radioGroupClassName={classes.radioGroup}
                periodType={selectedCycleIntervalPeriodType}
                defaultPeriodType={scanFrequencyDefaults.periodType}
                setPeriodType={setSelectedCycleIntervalPeriodType}
                options={frequencyOptions}
                value={selectedFrequency}
                setValue={setSelectedFrequency}
                periodTypeOptions={periodTypeOptions}
              />
            </Grid>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <ArchivedStatusMessage className={classes.archivedMessage} status={status} />
          </Grid>
        )}
        {isRetentionPromptVisible && (
          <Grid item xs={12}>
            <Grid container justifyContent="center">
              <DazzedParagraph16 className={classes.subtitle} textAlign={isMobile() ? 'start' : 'center'}>
                {t('statuses.updateStatusModal.retentionStlPrompt.question')}
              </DazzedParagraph16>
              <GrinRadioGroup
                radioGroupClassName={classes.radioGroup}
                value={retentionStlChoice}
                setValue={setRetentionStlChoice}
                options={[
                  {
                    value: RetentionStlChoices.UseLastStl,
                    label: t('statuses.updateStatusModal.retentionStlPrompt.useLastStl')
                  },
                  {
                    value: RetentionStlChoices.UploadLater,
                    label: t('statuses.updateStatusModal.retentionStlPrompt.uploadLater')
                  }
                ]}
              />
            </Grid>
          </Grid>
        )}
      </Grid>
    </BaseModal>
  )
}

export default UpdateStatusModal
